import React from 'react';

/**
 * Reusable clean table component with rank highlight & hover effects.
 * Props:
 *   columns: Array<{ key, label, align?, render? }>
 *   data: Array<object>
 *   keyField: string
 *   highlightTopRank: boolean
 *   emptyMessage: string
 */
const Table = ({
    columns = [],
    data = [],
    keyField = 'id',
    highlightTopRank = false,
    emptyMessage = 'No data available',
}) => {
    return (
        <div className="w-full overflow-x-auto rounded-xl border border-stone-200/80 bg-white shadow-xs">
            <table className="w-full text-left text-xs border-collapse">
                <thead>
                    <tr className="bg-stone-50/80 border-b border-stone-200/80 text-stone-500 font-semibold tracking-wider uppercase text-[11px]">
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={`px-4 py-3.5 ${
                                    col.align === 'right'
                                        ? 'text-right'
                                        : col.align === 'center'
                                        ? 'text-center'
                                        : 'text-left'
                                } ${col.className || ''}`}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                    {data.length === 0 ? (
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="px-4 py-8 text-center text-stone-400 font-normal italic"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        data.map((row, index) => {
                            const isTop3 = highlightTopRank && index < 3;
                            const topClass = isTop3
                                ? index === 0
                                    ? 'bg-amber-50/30 font-semibold'
                                    : index === 1
                                    ? 'bg-slate-50/40 font-semibold'
                                    : 'bg-orange-50/20 font-semibold'
                                : '';

                            return (
                                <tr
                                    key={row[keyField] || index}
                                    className={`hover:bg-stone-50/80 transition-colors duration-150 ${topClass}`}
                                >
                                    {columns.map((col) => {
                                        const cellValue = row[col.key];
                                        return (
                                            <td
                                                key={col.key}
                                                className={`px-4 py-3.5 whitespace-nowrap align-middle ${
                                                    col.align === 'right'
                                                        ? 'text-right'
                                                        : col.align === 'center'
                                                        ? 'text-center'
                                                        : 'text-left'
                                                } ${col.cellClassName || ''}`}
                                            >
                                                {col.render
                                                    ? col.render(cellValue, row, index)
                                                    : cellValue !== undefined && cellValue !== null
                                                    ? cellValue
                                                    : '-'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Table;
